import { createSignal, onMount, onCleanup, For, Show } from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import { useApi, isOk } from "../api";
import { useToast } from "../components/MessageBox";
import { useUserContext } from "../components/UserContextProvider";
import { useRoleParser } from "../hooks/permissions";
import { showConfirm } from "../components/Dialog";
import ResourceAccessPanel from '../components/ResourceAccessPanel';
import Text from "../components/Text";
import PingBadge from "../components/PingBadge";
import { hasRunningInstance } from "../components/BotStatusBadge";
import Modal from "../components/Modal";
import { EmptyState } from "../components/EmptyState";
import { SkeletonCard } from "../components/Skeleton";
import ProxyHealthChart from "../components/ProxyHealthChart";
import ProxyBotList from "../components/ProxyBotList";
import { useTranslation } from "../hooks/useTranslation";
import { formatDateTime } from "../services/dateFormat";
import type { ProxyInfo } from "../services/proxyApi";
import type {
  ExternalProxyDto,
  ExternalProxyPoolDto,
  ExternalProxyGatewayProtocol,
  ExternalProxyPoolProbeDto,
  ExternalProxySourceKind,
  ExternalProxyVotingPool,
  ProxyBotDto,
} from "../api/generated/schemas";
import styles from "./ProxyAdmin.module.css";
import { firstError } from '../api/envelope';

const PING_POLL_MS = 5000;

const SOURCE_WEBSHARE_API: ExternalProxySourceKind = 0;
const SOURCE_LIST_URL: ExternalProxySourceKind = 1;
const SOURCE_PASTED_LIST: ExternalProxySourceKind = 2;
const SOURCE_BRIGHTDATA_API: ExternalProxySourceKind = 3;
const VOTING_POOL_DATACENTER: ExternalProxyVotingPool = 0;
const VOTING_POOL_RESIDENTIAL: ExternalProxyVotingPool = 1;
const GATEWAY_SOCKS5: ExternalProxyGatewayProtocol = 0;
const GATEWAY_HTTP: ExternalProxyGatewayProtocol = 1;
const GATEWAY_SOCKS5_AND_HTTP: ExternalProxyGatewayProtocol = 2;

export default function ProxyAdmin() {
  const api = useApi();
  const toast = useToast();
  const { t } = useTranslation();
  const { user } = useUserContext();
  const { parseRoles, roles } = useRoleParser();

  const isPlatformAdmin = () => {
    const flags = parseRoles(user()?.permissions ?? 0, roles);
    return flags[roles.indexOf("Root")] ?? false;
  };

  const [proxies, setProxies] = createStore<ProxyInfo[]>([]);
  const [directBotCount, setDirectBotCount] = createSignal<number | null>(null);
  const [selectedId, setSelectedId] = createSignal<string | null>(null);
  const [editing, setEditing] = createSignal(false);
  const [creating, setCreating] = createSignal(false);
  const [externals, setExternals] = createStore<ExternalProxyDto[]>([]);
  const [proxyBots, setProxyBots] = createStore<ProxyBotDto[]>([]);
  const [chartProxyId, setChartProxyId] = createSignal<string | null>(null);
  const [reassigningBotId, setReassigningBotId] = createSignal<string | null>(null);
  const [restartingBotId, setRestartingBotId] = createSignal<string | null>(null);
  const [reassignedBotIds, setReassignedBotIds] = createSignal<string[]>([]);

  const [pools, setPools] = createStore<ExternalProxyPoolDto[]>([]);
  const [poolsLoading, setPoolsLoading] = createSignal(true);
  const [poolModalOpen, setPoolModalOpen] = createSignal(false);
  const [editingPoolId, setEditingPoolId] = createSignal<string | null>(null);
  const [syncingPoolId, setSyncingPoolId] = createSignal<string | null>(null);
  const [testingPoolId, setTestingPoolId] = createSignal<string | null>(null);
  const [poolProbes, setPoolProbes] = createSignal<Record<string, ExternalProxyPoolProbeDto>>({});

  const [poolTitle, setPoolTitle] = createSignal("");
  const [poolSource, setPoolSource] = createSignal<ExternalProxySourceKind>(SOURCE_PASTED_LIST);
  const [poolUrl, setPoolUrl] = createSignal("");
  const [poolApiKey, setPoolApiKey] = createSignal("");
  const [poolEntries, setPoolEntries] = createSignal("");
  const [poolAccountId, setPoolAccountId] = createSignal("");
  const [poolZone, setPoolZone] = createSignal("");
  const [poolGatewayHost, setPoolGatewayHost] = createSignal("");
  const [poolGatewayPort, setPoolGatewayPort] = createSignal("");
  const [poolGatewayProtocol, setPoolGatewayProtocol] = createSignal<ExternalProxyGatewayProtocol>(GATEWAY_SOCKS5);
  const [poolUsableForBots, setPoolUsableForBots] = createSignal(true);
  const [poolUsableForVoting, setPoolUsableForVoting] = createSignal(false);
  const [poolVotingPool, setPoolVotingPool] = createSignal<ExternalProxyVotingPool>(VOTING_POOL_DATACENTER);
  const [poolEnabled, setPoolEnabled] = createSignal(true);

  const [formName, setFormName] = createSignal("");
  const [formHost, setFormHost] = createSignal("");
  const [formPort, setFormPort] = createSignal(1080);
  const [formDesc, setFormDesc] = createSignal("");
  const [formEnabled, setFormEnabled] = createSignal(true);

  async function fetchProxies() {
    const res = await api.proxy.getAll();
    if (isOk(res.data)) setProxies(reconcile(res.data.value.proxies as ProxyInfo[], { key: "id" }));
  }

  async function fetchDirectInfo() {
    const res = await api.proxy.getDirectInfo();
    if (isOk(res.data)) setDirectBotCount(res.data.value.botsUsingCount);
  }

  async function fetchExternals() {
    const res = await api.proxy.getExternal();
    if (isOk(res.data)) setExternals(reconcile((res.data.value.proxies ?? []) as ExternalProxyDto[], { key: "id" }));
  }

  async function fetchProxyBots() {
    const res = await api.proxy.getBots();
    if (isOk(res.data)) setProxyBots(reconcile((res.data.value.bots ?? []) as ProxyBotDto[], { key: "botId" }));
  }

  async function fetchPools() {
    const res = await api.proxy.getPools();
    if (isOk(res.data)) setPools(reconcile((res.data.value.pools ?? []) as ExternalProxyPoolDto[], { key: "id" }));
    if (poolsLoading()) setPoolsLoading(false);
  }

  function botsOnProxy(proxyId: string) {
    return proxyBots.filter(b => b.proxyId === proxyId);
  }

  function runningBotsOnProxy(proxyId: string) {
    return botsOnProxy(proxyId).filter(b => hasRunningInstance(b.state)).length;
  }

  function botsInPool(poolId: string) {
    return proxyBots.filter(b => b.poolId === poolId);
  }

  function botsOnEntry(externalProxyId: string) {
    return proxyBots.filter(b => b.externalProxyId === externalProxyId);
  }

  function entriesOfPool(poolId: string) {
    return externals.filter(e => e.poolId === poolId);
  }

  // A bot on an unmonitored entry has no row to sit under, so the pool lists it instead of dropping it
  function botsInPoolWithoutEntry(poolId: string) {
    const listedEntryIds = new Set(entriesOfPool(poolId).map(e => e.id));
    return botsInPool(poolId).filter(b => !b.externalProxyId || !listedEntryIds.has(b.externalProxyId));
  }

  function emptyEntriesInPool(poolId: string) {
    return entriesOfPool(poolId).filter(e => botsOnEntry(e.id).length === 0).length;
  }

  async function reassignBot(bot: ProxyBotDto) {
    setReassigningBotId(bot.botId);
    const res = await api.proxy.reassignBot(bot.botId);
    setReassigningBotId(null);

    if (!isOk(res.data)) {
      toast?.error(firstError(res.data) ?? t("PROXY_REASSIGN_FAILED"));
      return;
    }

    const wasRunning = hasRunningInstance(bot.state);
    if (wasRunning) setReassignedBotIds(prev => [...prev, bot.botId]);
    toast?.success(t(
      wasRunning ? "PROXY_REASSIGN_DONE_RESTART_NEEDED" : "PROXY_REASSIGN_DONE",
      bot.minecraftUsername,
      res.data.value.externalProxyName));
    await fetchProxyBots();
  }

  async function restartBot(bot: ProxyBotDto) {
    setRestartingBotId(bot.botId);
    const res = await api.botLifecycle.restartBot(bot.botId);
    setRestartingBotId(null);

    if (!isOk(res.data)) {
      toast?.error(firstError(res.data) ?? t("PROXY_RESTART_FAILED"));
      return;
    }

    setReassignedBotIds(prev => prev.filter(id => id !== bot.botId));
    toast?.success(t("PROXY_RESTART_DONE", bot.minecraftUsername));
    await fetchProxyBots();
  }

  function sourceLabelKey(kind: ExternalProxySourceKind) {
    if (kind === SOURCE_WEBSHARE_API) return "PROXY_POOL_SOURCE_WEBSHARE";
    if (kind === SOURCE_BRIGHTDATA_API) return "PROXY_POOL_SOURCE_BRIGHTDATA";
    if (kind === SOURCE_LIST_URL) return "PROXY_POOL_SOURCE_URL";
    return "PROXY_POOL_SOURCE_PASTE";
  }

  function apiKeyHintKey() {
    return poolSource() === SOURCE_BRIGHTDATA_API ? "PROXY_POOL_APIKEY_BRIGHTDATA_HINT" : "PROXY_POOL_APIKEY_HINT";
  }

  async function testPool(poolId: string) {
    setTestingPoolId(poolId);
    const res = await api.proxy.testPool(poolId);
    setTestingPoolId(null);
    if (isOk(res.data)) {
      setPoolProbes({ ...poolProbes(), [poolId]: res.data.value });
    } else {
      toast?.error(firstError(res.data) ?? t("PROXY_POOL_TEST_FAILED"));
    }
  }

  function openNewPool() {
    setEditingPoolId(null);
    setPoolTitle("");
    setPoolSource(SOURCE_PASTED_LIST);
    setPoolUrl("");
    setPoolApiKey("");
    setPoolEntries("");
    setPoolAccountId("");
    setPoolZone("");
    setPoolGatewayHost("");
    setPoolGatewayPort("");
    setPoolGatewayProtocol(GATEWAY_SOCKS5);
    setPoolUsableForBots(true);
    setPoolUsableForVoting(false);
    setPoolVotingPool(VOTING_POOL_DATACENTER);
    setPoolEnabled(true);
    setPoolModalOpen(true);
  }

  function openEditPool(pool: ExternalProxyPoolDto) {
    setEditingPoolId(pool.id);
    setPoolTitle(pool.title);
    setPoolSource(pool.sourceKind);
    setPoolUrl(pool.sourceUrl ?? "");
    setPoolApiKey("");
    setPoolEntries("");
    setPoolAccountId(pool.providerAccountId ?? "");
    setPoolZone(pool.providerZone ?? "");
    setPoolGatewayHost(pool.gatewayHost ?? "");
    setPoolGatewayPort(pool.gatewayPort == null ? "" : String(pool.gatewayPort));
    setPoolGatewayProtocol(pool.gatewayProtocol);
    setPoolUsableForBots(pool.usableForBots);
    setPoolUsableForVoting(pool.usableForVoting);
    setPoolVotingPool(pool.votingPool);
    setPoolEnabled(pool.isEnabled);
    setPoolModalOpen(true);
  }

  async function savePool() {
    const title = poolTitle().trim();
    if (!title) {
      toast?.error(t("PROXY_POOL_TITLE_REQUIRED"));
      return;
    }

    const payload = {
      title,
      sourceKind: poolSource(),
      sourceUrl: poolSource() === SOURCE_LIST_URL ? poolUrl().trim() || null : null,
      apiKey: poolApiKey().trim() || null,
      inlineEntries: poolSource() === SOURCE_PASTED_LIST ? poolEntries().trim() || null : null,
      providerAccountId: poolSource() === SOURCE_BRIGHTDATA_API ? poolAccountId().trim() || null : null,
      providerZone: poolSource() === SOURCE_BRIGHTDATA_API ? poolZone().trim() || null : null,
      gatewayHost: poolGatewayHost().trim() || null,
      gatewayPort: poolGatewayPort().trim() ? parseInt(poolGatewayPort(), 10) : null,
      gatewayProtocol: poolGatewayProtocol(),
      usableForBots: poolUsableForBots(),
      usableForVoting: poolUsableForVoting(),
      votingPool: poolVotingPool(),
      isEnabled: poolEnabled(),
    };

    const id = editingPoolId();
    const res = id ? await api.proxy.updatePool(id, payload) : await api.proxy.createPool(payload);
    if (!isOk(res.data)) {
      toast?.error(firstError(res.data) ?? t("PROXY_POOL_SAVE_FAILED"));
      return;
    }

    toast?.info(id ? t("PROXY_POOL_UPDATED") : t("PROXY_POOL_CREATED"));
    setPoolModalOpen(false);
    await fetchPools();
    const savedId = id ?? (res.data.value as ExternalProxyPoolDto).id;
    await syncPool(savedId);
  }

  async function syncPool(poolId: string) {
    setSyncingPoolId(poolId);
    const res = await api.proxy.syncPool(poolId);
    setSyncingPoolId(null);
    if (isOk(res.data)) {
      const report = res.data.value;
      toast?.info(t("PROXY_POOL_SYNCED_TOAST", report.total, report.inserted, report.deactivated));
    } else {
      toast?.error(firstError(res.data) ?? t("PROXY_POOL_SYNC_FAILED"));
    }
    await fetchPools();
    await fetchExternals();
  }

  async function deletePool(pool: ExternalProxyPoolDto) {
    const confirmed = await showConfirm(
      t("PROXY_POOL_DELETE_CONFIRM", pool.title, pool.entryCount), true);
    if (!confirmed) return;

    const res = await api.proxy.deletePool(pool.id);
    if (isOk(res.data)) {
      toast?.info(t("PROXY_POOL_DELETED"));
      await fetchPools();
      await fetchExternals();
    } else {
      toast?.error(firstError(res.data) ?? t("PROXY_POOL_DELETE_FAILED"));
    }
  }

  let pollTimer: number | undefined;
  onMount(() => {
    fetchProxies();
    fetchDirectInfo();
    fetchExternals();
    fetchProxyBots();
    fetchPools();
    pollTimer = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      fetchProxies();
      fetchDirectInfo();
      fetchExternals();
      fetchProxyBots();
      fetchPools();
    }, PING_POLL_MS);
  });
  onCleanup(() => {
    if (pollTimer) window.clearInterval(pollTimer);
  });

  function selected() {
    return proxies.find(p => p.id === selectedId()) ?? null;
  }

  function loadForm(p: ProxyInfo) {
    setFormName(p.name);
    setFormHost(p.host);
    setFormPort(p.port);
    setFormDesc(p.description ?? "");
    setFormEnabled(p.isEnabled);
  }

  async function handleSelect(id: string) {
    setSelectedId(id);
    setCreating(false);
    setEditing(false);
    const p = proxies.find(x => x.id === id);
    if (p) loadForm(p);
  }

  function handleNew() {
    setSelectedId(null);
    setCreating(true);
    setEditing(true);

    setFormName("");
    setFormHost("");
    setFormPort(1080);
    setFormDesc("");
    setFormEnabled(true);
  }

  function cancelEdit() {
    if (creating()) {
      setCreating(false);
      setEditing(false);
      return;
    }
    setEditing(false);
    const s = selected();
    if (s) loadForm(s);
  }

  async function handleSave() {
    const payload = {
      name: formName().trim(),
      host: formHost().trim(),
      port: formPort(),
      description: formDesc().trim() || null,
      isEnabled: formEnabled(),
    };

    if (!payload.name || !payload.host) {
      toast?.error(t("PROXY_NAME_HOST_REQUIRED"));
      return;
    }

    if (creating()) {
      const res = await api.proxy.create(payload);
      if (isOk(res.data)) {
        toast?.info(t("PROXY_CREATED"));
        await fetchProxies();
        const created = res.data.value as ProxyInfo;
        setSelectedId(created.id);
        setCreating(false);
        setEditing(false);
      }
    } else {
      const id = selectedId();
      if (!id) return;
      const res = await api.proxy.update(id, payload);
      if (isOk(res.data)) {
        toast?.info(t("PROXY_UPDATED"));
        await fetchProxies();
        setEditing(false);
      }
    }
  }

  async function handleDelete() {
    const s = selected();
    if (!s) return;
    const affected = s.botsUsingCount;
    const msg = affected > 0
      ? t("PROXY_DELETE_CONFIRM_AFFECTED", s.name, affected)
      : t("PROXY_DELETE_CONFIRM", s.name);
    const confirmed = await showConfirm(msg, true);
    if (!confirmed) return;

    const res = await api.proxy.delete(s.id);
    if (isOk(res.data)) {
      toast?.info(t("PROXY_DELETED"));
      setSelectedId(null);
      setEditing(false);

      await fetchProxies();
    }
  }

  async function handleToggleEnabled() {
    const s = selected();
    if (!s) return;
    const next = !s.isEnabled;
    const running = runningBotsOnProxy(s.id);
    if (!next && running > 0) {
      const confirmed = await showConfirm(
        t("PROXY_DISABLE_CONFIRM", s.name, running), true);
      if (!confirmed) return;
    }
    const res = await api.proxy.update(s.id, {
      name: s.name,
      host: s.host,
      port: s.port,
      description: s.description,
      isEnabled: next,
    });
    if (isOk(res.data)) {
      toast?.info(next ? t("PROXY_ENABLED") : t("PROXY_DISABLED"));
      await fetchProxies();
    }
  }

  function showDetail() {
    return creating() || selectedId() !== null;
  }

  function isReadOnly() {
    return !editing() && !creating();
  }

  return (
    <Show when={isPlatformAdmin()} fallback={
      <div class={styles.forbidden}>
        <h1><Text val="FORBIDDEN" /></h1>
        <p><Text val="PLATFORM_ADMIN_ONLY" /></p>
      </div>
    }>
      <div class={styles.container}>
        <div class={styles.header}>
          <h1><Text val="PROXY_ADMIN_TITLE" /></h1>
          <button class={styles.createBtn} onClick={handleNew}>
            <i class="fa-solid fa-plus" /> <Text val="PROXY_NEW" />
          </button>
        </div>

        <div class={styles.layout}>
          <div class={styles.list}>
            <div class={`${styles.proxyRow} ${styles.directRow}`}>
              <div class={styles.proxyLeft}>
                <i class={`fa-solid fa-globe ${styles.proxyIcon} ${styles.directIcon}`} />
                <div class={styles.proxyInfo}>
                  <div class={styles.proxyName}><Text val="PROXY_DIRECT_TITLE" /></div>
                  <div class={styles.proxyMeta}><Text val="PROXY_DIRECT_HINT" /></div>
                </div>
              </div>
              <div class={styles.proxyBadges}>
                <Show when={directBotCount() !== null && directBotCount()! > 0}>
                  <span class={styles.countBadge} title={t("PROXY_DIRECT_BOTS_TITLE")}>
                    <i class="fa-solid fa-robot" /> {directBotCount()}
                  </span>
                </Show>
              </div>
            </div>
            <Show when={proxies.length === 0}>
              <div class={styles.empty}><Text val="PROXY_EMPTY" /></div>
            </Show>
            <For each={proxies}>
              {(p) => (
                <div
                  class={`${styles.proxyRow} ${selectedId() === p.id ? styles.proxyRowActive : ""}`}
                  onClick={() => handleSelect(p.id)}
                >
                  <div class={styles.proxyLeft}>
                    <i class={`fa-solid fa-network-wired ${styles.proxyIcon} ${p.isEnabled ? styles.proxyIconEnabled : styles.proxyIconDisabled}`} />
                    <div class={styles.proxyInfo}>
                      <div class={styles.proxyName}>{p.name}</div>
                      <div class={styles.proxyMeta}>
                        {p.host}:{p.port}
                      </div>
                    </div>
                  </div>
                  <div class={styles.proxyBadges}>
                    <Show when={p.botsUsingCount > 0}>
                      <span class={styles.countBadge} title={t("PROXY_PREMIUM_BOTS_TITLE")}>
                        <i class="fa-solid fa-robot" /> {p.botsUsingCount}
                      </span>
                    </Show>
                    <Show when={p.userGrantsCount + p.tenantGrantsCount > 0}>
                      <span class={styles.countBadge} title={t("PROXY_GRANTS_TITLE")}>
                        <i class="fa-solid fa-user-check" /> {p.userGrantsCount}/{p.tenantGrantsCount}
                      </span>
                    </Show>
                    <PingBadge ms={p.lastPingMs} />
                  </div>
                </div>
              )}
            </For>
          </div>

          <Show when={showDetail()}>
            <div class={styles.detail}>
              <div class={styles.detailHeader}>
                <div class={styles.detailTitle}>
                  {creating() ? <Text val="PROXY_NEW" /> : selected()?.name}
                </div>
                <Show when={!creating() && selected()}>
                  <span class={`${styles.statusPill} ${selected()!.isEnabled ? styles.statusPillEnabled : styles.statusPillDisabled}`}>
                    <Text val={selected()!.isEnabled ? "PROXY_STATUS_ENABLED" : "PROXY_STATUS_DISABLED"} />
                  </span>
                </Show>
              </div>

              <div class={styles.fieldGroup}>
                <label class={styles.fieldLabel}><Text val="PROXY_FIELD_NAME" /></label>
                <input
                  class={styles.fieldInput}
                  value={formName()}
                  onInput={(e) => setFormName(e.currentTarget.value)}
                  disabled={isReadOnly()}
                  placeholder="piko-gamma"
                />
              </div>

              <div class={styles.fieldGroup}>
                <label class={styles.fieldLabel}><Text val="PROXY_FIELD_HOST" /></label>
                <input
                  class={styles.fieldInput}
                  value={formHost()}
                  onInput={(e) => setFormHost(e.currentTarget.value)}
                  disabled={isReadOnly()}
                  placeholder="10.10.0.2"
                />
                <span class={styles.fieldHint}><Text val="PROXY_HOST_HINT" /></span>
              </div>

              <div class={styles.fieldGroup}>
                <label class={styles.fieldLabel}><Text val="PROXY_FIELD_PORT" /></label>
                <input
                  class={styles.fieldInput}
                  type="number"
                  value={formPort()}
                  onInput={(e) => setFormPort(parseInt(e.currentTarget.value) || 1080)}
                  disabled={isReadOnly()}
                />
              </div>

              <div class={styles.fieldGroup}>
                <label class={styles.fieldLabel}><Text val="PROXY_FIELD_DESCRIPTION" /></label>
                <textarea
                  class={styles.fieldTextarea}
                  value={formDesc()}
                  onInput={(e) => setFormDesc(e.currentTarget.value)}
                  disabled={isReadOnly()}
                  rows="2"
                  placeholder={t("PROXY_DESC_PLACEHOLDER")}
                />
              </div>

              <Show when={creating()}>
                <label class={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={formEnabled()}
                    onChange={(e) => setFormEnabled(e.currentTarget.checked)}
                  />
                  <Text val="PROXY_FIELD_ENABLED" />
                </label>
              </Show>

              <div class={styles.actions}>
                <Show when={editing() || creating()}>
                  <button class={`${styles.btn} ${styles.btnPrimary}`} onClick={handleSave}>
                    <i class="fa-solid fa-check" /> <Text val="SAVE" />
                  </button>
                  <button class={styles.btn} onClick={cancelEdit}>
                    <Text val="CANCEL" />
                  </button>
                </Show>
                <Show when={!editing() && !creating() && selectedId()}>
                  <button class={styles.btn} onClick={() => setEditing(true)}>
                    <i class="fa-solid fa-pen" /> <Text val="EDIT" />
                  </button>
                  <button class={styles.btn} onClick={handleToggleEnabled}>
                    <Show when={selected()?.isEnabled}
                      fallback={<><i class="fa-solid fa-play" /> <Text val="PROXY_ENABLE" /></>}>
                      <i class="fa-solid fa-pause" /> <Text val="PROXY_DISABLE" />
                    </Show>
                  </button>
                  <button class={`${styles.btn} ${styles.btnDanger}`} onClick={handleDelete}>
                    <i class="fa-solid fa-trash" /> <Text val="DELETE" />
                  </button>
                </Show>
              </div>

              <Show when={selectedId() && !creating()}>
                <div class={styles.botSection}>
                  <ProxyBotList
                    bots={botsOnProxy(selectedId()!)}
                    title={t("PROXY_BOTS_ASSIGNED_TITLE")}
                    empty={t("PROXY_BOTS_NONE_PREMIUM")}
                  />
                </div>
              </Show>

              <Show when={selectedId() && !creating()}>
                <div class={styles.accessSection}>
                  <ResourceAccessPanel resourceId={selectedId()!} />
                </div>
              </Show>

              <Show when={selectedId() && !creating()}>
                <div class={styles.chartSection}>
                  <ProxyHealthChart proxyId={selectedId()!} />
                </div>
              </Show>
            </div>
          </Show>
        </div>

        <div class={styles.externalSection}>
          <div class={styles.externalHeader}>
            <h2><Text val="PROXY_POOLS_TITLE" /></h2>
            <span class={styles.entryCount}>{pools.length}</span>
            <button class="btn btn-primary btn-sm" onClick={openNewPool}>
              <i class="fa-solid fa-plus" /> <Text val="PROXY_POOL_NEW" />
            </button>
          </div>
          <p class={styles.externalHint}><Text val="PROXY_POOLS_HINT" /></p>

          <Show when={!poolsLoading()} fallback={<div class={styles.externalList}><SkeletonCard /><SkeletonCard /></div>}>
            <Show
              when={pools.length > 0}
              fallback={<EmptyState icon="fa-layer-group" title={t("PROXY_POOL_EMPTY")} hint={t("PROXY_POOL_EMPTY_HINT")} />}
            >
              <div class={styles.externalList}>
                <For each={pools}>
                  {(pool) => (
                    <div class={styles.poolCard}>
                      <div class={styles.poolHeader}>
                        <div class={styles.proxyLeft}>
                          <i class={`fa-solid fa-layer-group ${styles.proxyIcon} ${pool.isEnabled ? styles.proxyIconEnabled : styles.proxyIconDisabled}`} />
                          <div class={styles.proxyInfo}>
                            <div class={styles.proxyName}>{pool.title}</div>
                            <div class={styles.proxyMeta}>
                              <Text val={sourceLabelKey(pool.sourceKind)} />
                              {" · "}
                              {t("PROXY_POOL_ENTRIES_COUNT", pool.activeEntryCount, pool.entryCount)}
                              {" · "}
                              {pool.lastSyncedAt
                                ? t("PROXY_POOL_SYNCED_AT", formatDateTime(pool.lastSyncedAt))
                                : t("PROXY_POOL_NEVER_SYNCED")}
                            </div>
                          </div>
                        </div>
                        <div class={styles.poolBadges}>
                          <Show when={pool.usableForBots}>
                            <span class={`${styles.statusPill} ${styles.statusPillEnabled}`}>
                              <Text val="PROXY_POOL_BADGE_BOTS" />
                            </span>
                          </Show>
                          <Show when={pool.usableForVoting}>
                            <span class={`${styles.statusPill} ${styles.poolPillVoting}`}>
                              <Text val={pool.votingPool === VOTING_POOL_RESIDENTIAL ? "PROXY_POOL_BADGE_VOTING_RESIDENTIAL" : "PROXY_POOL_BADGE_VOTING_DATACENTER"} />
                            </span>
                          </Show>
                          <Show when={!pool.isEnabled}>
                            <span class={`${styles.statusPill} ${styles.statusPillDisabled}`}>
                              <Text val="PROXY_POOL_BADGE_DISABLED" />
                            </span>
                          </Show>
                          <Show when={pool.botsUsingCount > 0}>
                            <span class={styles.countBadge} title={t("PROXY_POOL_BOTS_TITLE")}>
                              <i class="fa-solid fa-robot" /> {pool.botsUsingCount}
                            </span>
                          </Show>
                        </div>
                      </div>

                      <Show when={pool.lastSyncError}>
                        <div class={styles.poolError}>
                          <i class="fa-solid fa-triangle-exclamation" /> {pool.lastSyncError}
                        </div>
                      </Show>

                      <div class={styles.poolActions}>
                        <button
                          class="btn btn-secondary btn-sm"
                          disabled={syncingPoolId() === pool.id}
                          onClick={() => syncPool(pool.id)}
                        >
                          <Show when={syncingPoolId() === pool.id}
                            fallback={<><i class="fa-solid fa-rotate" /> <Text val="PROXY_POOL_SYNC" /></>}>
                            <i class="fa-solid fa-spinner fa-spin" /> <Text val="PROXY_POOL_SYNCING" />
                          </Show>
                        </button>
                        <button
                          class="btn btn-secondary btn-sm"
                          disabled={testingPoolId() === pool.id}
                          onClick={() => testPool(pool.id)}
                        >
                          <Show when={testingPoolId() === pool.id}
                            fallback={<><i class="fa-solid fa-vial" /> <Text val="PROXY_POOL_TEST" /></>}>
                            <i class="fa-solid fa-spinner fa-spin" /> <Text val="PROXY_POOL_TESTING" />
                          </Show>
                        </button>
                        <button class="btn btn-secondary btn-sm" onClick={() => openEditPool(pool)}>
                          <i class="fa-solid fa-pen" /> <Text val="EDIT" />
                        </button>
                        <button class="btn btn-danger btn-sm" onClick={() => deletePool(pool)}>
                          <i class="fa-solid fa-trash" /> <Text val="DELETE" />
                        </button>
                      </div>

                      <Show when={poolProbes()[pool.id]} keyed>
                        {(probe) => (
                          <div class={probe.ok ? styles.poolProbeOk : styles.poolError}>
                            <i class={probe.ok ? "fa-solid fa-circle-check" : "fa-solid fa-circle-xmark"} />
                            <Show when={probe.ok} fallback={<span>{probe.error}</span>}>
                              <span>
                                {t("PROXY_POOL_TEST_RESULT", probe.exitAddress ?? "", Math.round(probe.elapsedMs ?? 0))}
                              </span>
                            </Show>
                            <span class={styles.poolProbeEndpoint}>{probe.endpoint} · {probe.username}</span>
                          </div>
                        )}
                      </Show>

                      <Show when={entriesOfPool(pool.id).length > 0} fallback={
                        <div class={styles.poolNoEntries}>
                          <Text val={pool.usableForBots ? "PROXY_POOL_NO_MONITORED_ENTRIES" : "PROXY_POOL_VOTING_ONLY_HINT"} />
                        </div>
                      }>
                        <div class={styles.poolEntries}>
                          <For each={entriesOfPool(pool.id)}>
                            {(ext) => (
                              <div class={styles.externalEntry}>
                                <div class={styles.externalRow}>
                                  <div class={styles.proxyLeft}>
                                    <i class={`fa-solid fa-server ${styles.proxyIcon} ${styles.proxyIconEnabled}`} />
                                    <div class={styles.proxyInfo}>
                                      <div class={styles.proxyName}>{ext.name}</div>
                                      <div class={styles.proxyMeta}>{ext.host}:{ext.port}</div>
                                    </div>
                                  </div>
                                  <div class={styles.proxyBadges}>
                                    <Show when={botsOnEntry(ext.id).length > 0}>
                                      <span class={styles.countBadge} title={t("PROXY_BOTS_ON_ENTRY_TITLE")}>
                                        <i class="fa-solid fa-robot" /> {botsOnEntry(ext.id).length}
                                      </span>
                                    </Show>
                                    <PingBadge ms={ext.lastPingMs} />
                                    <button class="btn btn-secondary btn-sm" onClick={() => setChartProxyId(ext.id)}>
                                      <i class="fa-solid fa-chart-line" /> <Text val="PROXY_EXTERNAL_VIEW_HEALTH" />
                                    </button>
                                  </div>
                                </div>

                                <Show when={botsOnEntry(ext.id).length > 0}>
                                  <div class={styles.entryBotSection}>
                                    <ProxyBotList
                                      bots={botsOnEntry(ext.id)}
                                      title={t("PROXY_BOTS_ON_ENTRY_TITLE")}
                                      empty={t("PROXY_BOTS_NONE_ENTRY")}
                                      renderActions={(bot) => (
                                        <>
                                          <Show when={reassignedBotIds().includes(bot.botId)}>
                                            <button
                                              class="btn btn-primary btn-sm"
                                              disabled={restartingBotId() === bot.botId}
                                              onClick={() => restartBot(bot)}>
                                              <i class="fa-solid fa-rotate-right" /> <Text val="PROXY_BOT_RESTART" />
                                            </button>
                                          </Show>
                                          <button
                                            class="btn btn-secondary btn-sm"
                                            disabled={reassigningBotId() === bot.botId || emptyEntriesInPool(pool.id) === 0}
                                            title={emptyEntriesInPool(pool.id) === 0 ? t("PROXY_BOT_REASSIGN_POOL_FULL") : t("PROXY_BOT_REASSIGN_TITLE")}
                                            onClick={() => reassignBot(bot)}>
                                            <i class="fa-solid fa-right-left" /> <Text val="PROXY_BOT_REASSIGN" />
                                          </button>
                                        </>
                                      )}
                                    />
                                  </div>
                                </Show>
                              </div>
                            )}
                          </For>
                        </div>
                      </Show>

                      <Show when={botsInPoolWithoutEntry(pool.id).length > 0}>
                        <div class={styles.poolBotSection}>
                          <ProxyBotList
                            bots={botsInPoolWithoutEntry(pool.id)}
                            title={t("PROXY_BOTS_UNMONITORED_ENTRY_TITLE")}
                            empty={t("PROXY_BOTS_NONE_POOL")}
                          />
                        </div>
                      </Show>
                    </div>
                  )}
                </For>
              </div>
            </Show>
          </Show>
        </div>
      </div>

      <Modal open={chartProxyId() !== null} onClose={() => setChartProxyId(null)} title={<Text val="PROXY_HEALTH_TITLE" />}>
        <Show when={chartProxyId()} keyed>
          {(id) => <ProxyHealthChart proxyId={id} />}
        </Show>
      </Modal>

      <Modal
        open={poolModalOpen()}
        onClose={() => setPoolModalOpen(false)}
        title={<Text val={editingPoolId() ? "PROXY_POOL_EDIT" : "PROXY_POOL_NEW"} />}
      >
        <form class="v" style={{ gap: "var(--spacing-md)" }} onSubmit={(e) => { e.preventDefault(); void savePool(); }}>
          <div class={styles.fieldGroup}>
            <label class={styles.fieldLabel}><Text val="PROXY_POOL_FIELD_TITLE" /></label>
            <input
              class={styles.fieldInput}
              value={poolTitle()}
              onInput={(e) => setPoolTitle(e.currentTarget.value)}
              placeholder="BrightData DC 1"
            />
          </div>

          <div class={styles.fieldGroup}>
            <label class={styles.fieldLabel}><Text val="PROXY_POOL_FIELD_SOURCE" /></label>
            <select
              class={styles.fieldSelect}
              value={String(poolSource())}
              onChange={(e) => setPoolSource(Number(e.currentTarget.value) as ExternalProxySourceKind)}
            >
              <option value={String(SOURCE_PASTED_LIST)}>{t("PROXY_POOL_SOURCE_PASTE")}</option>
              <option value={String(SOURCE_LIST_URL)}>{t("PROXY_POOL_SOURCE_URL")}</option>
              <option value={String(SOURCE_WEBSHARE_API)}>{t("PROXY_POOL_SOURCE_WEBSHARE")}</option>
              <option value={String(SOURCE_BRIGHTDATA_API)}>{t("PROXY_POOL_SOURCE_BRIGHTDATA")}</option>
            </select>
          </div>

          <Show when={poolSource() === SOURCE_PASTED_LIST}>
            <div class={styles.fieldGroup}>
              <label class={styles.fieldLabel}><Text val="PROXY_POOL_FIELD_ENTRIES" /></label>
              <textarea
                class={styles.fieldTextarea}
                rows="6"
                value={poolEntries()}
                onInput={(e) => setPoolEntries(e.currentTarget.value)}
                placeholder="brd.superproxy.io:22228:brd-customer-hl_00000000-zone-datacenter_zone:password"
              />
              <span class={styles.fieldHint}><Text val="PROXY_POOL_ENTRIES_HINT" /></span>
              <Show when={editingPoolId()}>
                <span class={styles.fieldHint}><Text val="PROXY_POOL_ENTRIES_REPLACE_HINT" /></span>
              </Show>
            </div>
          </Show>

          <Show when={poolSource() === SOURCE_LIST_URL}>
            <div class={styles.fieldGroup}>
              <label class={styles.fieldLabel}><Text val="PROXY_POOL_FIELD_URL" /></label>
              <input
                class={styles.fieldInput}
                value={poolUrl()}
                onInput={(e) => setPoolUrl(e.currentTarget.value)}
                placeholder="https://proxy.webshare.io/api/v2/proxy/list/download/..."
              />
              <span class={styles.fieldHint}><Text val="PROXY_POOL_URL_HINT" /></span>
            </div>
          </Show>

          <Show when={poolSource() === SOURCE_BRIGHTDATA_API}>
            <div class={styles.fieldGroup}>
              <label class={styles.fieldLabel}><Text val="PROXY_POOL_FIELD_ACCOUNT_ID" /></label>
              <input
                class={styles.fieldInput}
                value={poolAccountId()}
                onInput={(e) => setPoolAccountId(e.currentTarget.value)}
                placeholder="hl_00000000"
              />
              <span class={styles.fieldHint}><Text val="PROXY_POOL_ACCOUNT_ID_HINT" /></span>
            </div>

            <div class={styles.fieldGroup}>
              <label class={styles.fieldLabel}><Text val="PROXY_POOL_FIELD_ZONE" /></label>
              <input
                class={styles.fieldInput}
                value={poolZone()}
                onInput={(e) => setPoolZone(e.currentTarget.value)}
                placeholder="datacenter_zone"
              />
            </div>
          </Show>

          <Show when={poolSource() === SOURCE_WEBSHARE_API || poolSource() === SOURCE_BRIGHTDATA_API}>
            <div class={styles.fieldGroup}>
              <label class={styles.fieldLabel}><Text val="PROXY_POOL_FIELD_APIKEY" /></label>
              <input
                class={styles.fieldInput}
                type="password"
                value={poolApiKey()}
                onInput={(e) => setPoolApiKey(e.currentTarget.value)}
                autocomplete="off"
              />
              <span class={styles.fieldHint}>
                <Text val={editingPoolId() ? "PROXY_POOL_APIKEY_KEEP_HINT" : apiKeyHintKey()} />
              </span>
            </div>
          </Show>

          <div class={styles.fieldGroup}>
            <label class={styles.fieldLabel}>
              <Text val={poolSource() === SOURCE_BRIGHTDATA_API ? "PROXY_POOL_FIELD_GATEWAY_REQUIRED" : "PROXY_POOL_FIELD_GATEWAY"} />
            </label>
            <div class="h" style={{ gap: "var(--spacing-sm)" }}>
              <input
                class={styles.fieldInput}
                value={poolGatewayHost()}
                onInput={(e) => setPoolGatewayHost(e.currentTarget.value)}
                placeholder="brd.superproxy.io"
              />
              <input
                class={styles.fieldInput}
                style={{ "max-width": "7rem" }}
                type="number"
                value={poolGatewayPort()}
                onInput={(e) => setPoolGatewayPort(e.currentTarget.value)}
                placeholder="22228"
              />
            </div>
            <span class={styles.fieldHint}>
              <Text val={poolSource() === SOURCE_BRIGHTDATA_API ? "PROXY_POOL_GATEWAY_BRIGHTDATA_HINT" : "PROXY_POOL_GATEWAY_HINT"} />
            </span>
          </div>

          <div class={styles.fieldGroup}>
            <label class={styles.fieldLabel}><Text val="PROXY_POOL_FIELD_GATEWAY_PROTOCOL" /></label>
            <select
              class={styles.fieldSelect}
              value={String(poolGatewayProtocol())}
              onChange={(e) => setPoolGatewayProtocol(Number(e.currentTarget.value) as ExternalProxyGatewayProtocol)}
            >
              <option value={String(GATEWAY_SOCKS5)}>{t("PROXY_POOL_GATEWAY_SOCKS5")}</option>
              <option value={String(GATEWAY_HTTP)}>{t("PROXY_POOL_GATEWAY_HTTP")}</option>
              <option value={String(GATEWAY_SOCKS5_AND_HTTP)}>{t("PROXY_POOL_GATEWAY_BOTH")}</option>
            </select>
            <span class={styles.fieldHint}><Text val="PROXY_POOL_GATEWAY_PROTOCOL_HINT" /></span>
          </div>

          <label class={styles.checkboxRow}>
            <input type="checkbox" checked={poolUsableForBots()} onChange={(e) => setPoolUsableForBots(e.currentTarget.checked)} />
            <Text val="PROXY_POOL_FIELD_USABLE_BOTS" />
          </label>

          <label class={styles.checkboxRow}>
            <input type="checkbox" checked={poolUsableForVoting()} onChange={(e) => setPoolUsableForVoting(e.currentTarget.checked)} />
            <Text val="PROXY_POOL_FIELD_USABLE_VOTING" />
          </label>

          <Show when={poolUsableForVoting()}>
            <div class={styles.fieldGroup}>
              <label class={styles.fieldLabel}><Text val="PROXY_POOL_FIELD_VOTING_POOL" /></label>
              <select
                class={styles.fieldSelect}
                value={String(poolVotingPool())}
                onChange={(e) => setPoolVotingPool(Number(e.currentTarget.value) as ExternalProxyVotingPool)}
              >
                <option value={String(VOTING_POOL_DATACENTER)}>{t("PROXY_POOL_VOTING_DATACENTER")}</option>
                <option value={String(VOTING_POOL_RESIDENTIAL)}>{t("PROXY_POOL_VOTING_RESIDENTIAL")}</option>
              </select>
              <span class={styles.fieldHint}><Text val="PROXY_POOL_VOTING_POOL_HINT" /></span>
            </div>
          </Show>

          <label class={styles.checkboxRow}>
            <input type="checkbox" checked={poolEnabled()} onChange={(e) => setPoolEnabled(e.currentTarget.checked)} />
            <Text val="PROXY_POOL_FIELD_ENABLED" />
          </label>

          <div class="h" style={{ "justify-content": "flex-end", gap: "var(--spacing-sm)" }}>
            <button type="button" class="btn btn-secondary" onClick={() => setPoolModalOpen(false)}>
              <Text val="CANCEL" />
            </button>
            <button type="submit" class="btn btn-primary">
              <i class="fa-solid fa-check" /> <Text val="SAVE" />
            </button>
          </div>
        </form>
      </Modal>
    </Show>
  );
}
